import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { slug } from "@/lib/api";

export const ACCESSORY_KEYS = ["lighting", "hydroturf", "wet_sound", "batteries", "covers", "misc"];

export const QUOTE_TYPES = [
  { key: "boat", label: "Boat", blurb: "A new Gator Trax or Pro-Drive hull, with or without options", cats: ["boat"] },
  { key: "motor", label: "Motor", blurb: "Mud Buddy, Pro-Drive, GatorTail and Freedom — surface drive or long-tail", cats: ["motor"], graphic: "/tiles/motor-5400.webp" },
  { key: "trailer", label: "Trailer", blurb: "A Road King trailer sized for your boat", cats: ["trailer"], graphic: "/tiles/trailer-roadking.webp", photo: true },
  { key: "accessories", label: "Accessories & Parts", blurb: "Lighting, Hydroturf, Wet Sounds, batteries, covers, misc", cats: ACCESSORY_KEYS },
];

export const QuotePicker = ({ categories, onContinue }) => {
  const [picked, setPicked] = useState([]);
  const allKeys = categories.map((c) => c.key);
  const togglePick = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const chosenCats = allKeys.filter((k) => QUOTE_TYPES.some((t) => picked.includes(t.key) && t.cats.includes(k)));

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16" data-testid="quote-picker">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1B66C9]">Let&apos;s get started</p>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">What do you want a quote on?</h1>
      <p className="mt-2 max-w-xl text-muted-foreground">
        Pick one thing or several — no minimums. You can always add more before you send it.
      </p>

      <button
        data-testid="pick-complete-package"
        onClick={() => onContinue(allKeys)}
        className="group relative mt-8 flex w-full flex-col overflow-hidden rounded-lg border-2 border-[#1E231F] bg-[#1F261F] text-left text-white transition-transform duration-200 hover:-translate-y-0.5 dark:border-white/20 sm:flex-row sm:items-stretch"
      >
        <span className="relative h-56 w-full overflow-hidden sm:absolute sm:inset-y-0 sm:right-0 sm:h-full sm:w-[34%]">
          <img
            src="/tiles/complete-package-boat-upright.webp"
            alt="Camo Gator Trax boat with mud motor"
            data-testid="pick-complete-package-graphic"
            className="h-full w-full object-contain object-center p-2 transition-transform duration-500 group-hover:scale-[1.03] sm:py-2.5 sm:pr-3"
          />
          <span className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1F261F] sm:bg-gradient-to-r sm:from-[#1F261F] sm:via-transparent sm:via-12% sm:to-transparent" />
        </span>
        <span className="relative flex w-full items-center gap-4 p-5 sm:min-h-[220px] sm:w-[66%] sm:p-6">
          <span className="flex-1">
            <span className="block font-display text-xl font-black sm:text-2xl">Complete Package</span>
            <span className="mt-1 block text-sm text-white/75">Boat + motor + trailer + gear, guided step by step</span>
          </span>
          <ArrowRight className="flex-shrink-0 transition-transform group-hover:translate-x-1" size={20} />
        </span>
      </button>

      <div className="mt-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <span className="h-px flex-1 bg-border" /> or just the pieces you need <span className="h-px flex-1 bg-border" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {QUOTE_TYPES.map((t) => {
          const on = picked.includes(t.key);
          return (
            <button
              key={t.key}
              data-testid={`pick-${slug(t.key)}`}
              onClick={() => togglePick(t.key)}
              className={`group relative flex items-start gap-4 rounded-lg border bg-card p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                on ? "border-2 border-[#1B66C9] shadow-md" : "border-border"
              }`}
            >
              <span className="flex-1">
                <span className="block font-display text-base font-bold">{t.label}</span>
                <span className="mt-0.5 block text-sm text-muted-foreground">{t.blurb}</span>
              </span>
              {t.graphic && (
                <img
                  src={t.graphic}
                  alt=""
                  data-testid={`pick-${slug(t.key)}-graphic`}
                  className={`flex-shrink-0 self-center transition-transform duration-300 group-hover:scale-105 ${
                    t.photo ? "h-16 w-40 rounded-md object-cover sm:h-[68px] sm:w-44" : "h-20 w-24 object-contain drop-shadow-md"
                  }`}
                />
              )}
              {on && (
                <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#1B66C9] text-white">
                  <Check size={13} strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="pick-summary">
          {picked.length === 0 ? "Select at least one to continue." : `Quoting: ${QUOTE_TYPES.filter((t) => picked.includes(t.key)).map((t) => t.label).join(", ")}`}
        </p>
        <Button
          data-testid="pick-continue-button"
          disabled={chosenCats.length === 0}
          onClick={() => onContinue(chosenCats)}
          className="h-12 rounded-md bg-[#1B66C9] px-8 font-bold hover:bg-[#144E9C]"
        >
          Continue <ArrowRight className="ml-2" size={18} />
        </Button>
      </div>
    </div>
  );
};
